import React, { Component, Fragment } from 'react';
import { RuleCategory } from '../../../../services/RuleCategory';
import AlertLevel from '../../../../shared/alert-level/AlertLevel';
import PlayerIdAndName from '../../../../shared/player-id-and-name/PlayerIdAndName';
import FriendlyDate from '../../../../shared/FriendlyDate';
import { TimeZoneNote } from '../../../../shared/time-zone-note/TimeZoneNote';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { fetchEvents } from '../redux/events/actions';
import { Pager } from '../../../../shared/pager/Pager';
import {
    defaultFirstPage,
    defaultItemsPerPage
} from '../../../../services/Paging';
import { isEqual } from 'lodash';
import {
    getSelectedBrandId,
    getSelectedJurisdictionCode
} from '../../../../jurisdiction/redux/selectors';
import { EmptyList } from '../../../../shared/empty-list/EmptyList';
import { Sorter } from '../../../../shared/table/Sorter';
import { FilterBadgeContainer } from '../../../../shared/filter-badge-container/FilterBadgeContainer';
import {
    extendWithModalSupport,
    ModalContainer
} from '../../../../shared/modal-container/ModalContainer';
import { Filters } from '../../../../shared/filters/Filters';
import { makeEventTypeDropdownOptions } from '../../../../shared/models/event-type';
import { EventTypeLabelPipe } from '../../../../shared/pipes/EventTypeLabelPipe';
import './EventListContainer.scss';
import { FilterButton } from '../../../../shared/button/filter-button/FilterButton';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import { RiskFactorInfoModalContent } from './RiskFactorInfoModalContent';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';

export const eventsTableHeaders = [
    { key: 'EVENT_REFERENCE', value: 'Player & Id', showArrowSort: true },
    { key: 'EVENT_TIMESTAMP', value: 'Event Timestamp', showArrowSort: true },
    { key: 'EVENT_TYPE', value: 'Event Type', showArrowSort: false },
    { key: 'DESCRIPTION', value: 'Description', showArrowSort: false },
    { key: 'AVERAGE_RISK', value: 'Risk Level', showArrowSort: true },
    {
        key: 'PLAYER_OVERALL_RISK',
        value: 'Player Overall Risk',
        showArrowSort: false
    },
    { key: 'ACTIONS', value: null, showArrowSort: false }
];

const ModalMode = Object.freeze({
    FILTER: 'FILTER',
    INFO: 'INFO'
});

class EventListContainer extends Component {
    constructor(props) {
        super(props);
        extendWithModalSupport(this);
        this.state = {
            modalMode: ModalMode.FILTER,
            selectedEvent: null,
            pagination: {
                pageSize: defaultItemsPerPage,
                page: defaultFirstPage
            },
            sort: {
                currentSortingKey: null
            },
            filters: {
                type: null
            }
        };
    }
    componentDidMount() {
        const {
            ruleCategory,
            sort,
            pagination,
            jurisdictionCode,
            brandId
        } = this.props;
        this.props.fetchEvents(
            ruleCategory,
            sort,
            pagination,
            jurisdictionCode,
            brandId
        );
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            !isEqual(prevProps.jurisdictionCode, this.props.jurisdictionCode) ||
            !isEqual(prevProps.brandId, this.props.brandId)
        ) {
            this.fetchEventsFromState();
        }
    }

    openFilterModal = () =>
        this.setState(
            {
                ...this.state,
                modalMode: ModalMode.FILTER
            },
            () => this.modalInstance.openModal()
        );

    openInfoModal = selectedEvent =>
        this.setState(
            {
                ...this.state,
                modalMode: ModalMode.INFO,
                selectedEvent
            },
            () => this.modalInstance.openModal()
        );

    fetchEventsFromState() {
        const { ruleCategory, jurisdictionCode, brandId } = this.props;
        const { currentSortingKey, currentDirection } = this.state.sort;

        this.props.fetchEvents(
            ruleCategory,
            {
                sortType: currentSortingKey,
                sortOrder: currentDirection
            },
            this.state.pagination,
            jurisdictionCode,
            brandId,
            this.state.filters.type
        );
    }

    // todo [mandy]: add category name to the Risk Level column name.

    updatePagination = pagination =>
        this.setState(
            {
                ...this.state,
                pagination
            },
            () => this.fetchEventsFromState()
        );

    updateSort = ({ sortingKey, sortDirection }) =>
        this.setState(
            {
                ...this.state,
                sort: {
                    currentSortingKey: sortingKey,
                    currentDirection: sortDirection
                }
            },
            () => this.fetchEventsFromState()
        );

    makeFilters = () => {
        const typeConfig = {
            label: 'Event Type',
            options: makeEventTypeDropdownOptions()
        };

        return (
            <Filters
                typeConfig={typeConfig}
                severityConfig={null}
                statusConfig={null}
                applyFilters={this.handleApplyFilters}
                clearFilters={this.handleClearFilters}
            />
        );
    };

    makeInfoModalContent = () => (
        <RiskFactorInfoModalContent
            selectedEvent={this.state.selectedEvent}
            closeModal={() => this.modalInstance.closeModal()}
        />
    );

    getModalContent = () => {
        switch (this.state.modalMode) {
            case ModalMode.FILTER:
                return this.makeFilters();
            case ModalMode.INFO:
                return this.makeInfoModalContent();
            default:
                return null;
        }
    };

    getModalTitle = () => {
        switch (this.state.modalMode) {
            case ModalMode.FILTER:
                return 'Filters';
            case ModalMode.INFO:
                return 'Event Details';
            default:
                return null;
        }
    };

    handleApplyFilters = filters => {
        this.setState(
            {
                ...this.state,
                filters,
                pagination: {
                    ...this.state.pagination,
                    page: defaultFirstPage
                }
            },
            () => {
                this.modalInstance.closeModal();
                this.fetchEventsFromState();
            }
        );
    };

    handleClearFilters = filters => {
        this.setState(
            {
                ...this.state,
                filters,
                pagination: {
                    ...this.state.pagination,
                    page: defaultFirstPage
                }
            },
            () => {
                this.modalInstance.closeModal();
                this.fetchEventsFromState();
            }
        );
    };

    handleRemoveFilter = filterValue => {
        // todo: Needs refactoring but works for now
        const { filters } = this.state;

        const filterType = Object.keys(filters).find(key => {
            return filters[key] === filterValue;
        });

        this.setState(
            {
                ...this.state,
                filters: {
                    ...filters,
                    [filterType]: null
                }
            },
            () => this.fetchEventsFromState()
        );
    };

    render() {
        const { events, pagination, loading, ruleCategory } = this.props;

        return (
            <Fragment>
                <div className="event-list table">
                    <TimeZoneNote />
                    <table style={this.tableStyle}>
                        <tbody>
                            <tr className={'table-options'}>
                                <td colSpan={eventsTableHeaders.length - 1}>
                                    <FilterBadgeContainer
                                        filters={this.state.filters}
                                        TypeLabelPipe={EventTypeLabelPipe}
                                        removeFilter={this.handleRemoveFilter}
                                    />
                                </td>
                                {/*<td>*/}
                                {/*todo: Commented out until table settings are implemented*/}
                                {/*<FilterButton onClick={() => {}}>*/}
                                {/*    <i className={'material-icons'}>*/}
                                {/*        view_list*/}
                                {/*    </i>*/}
                                {/*    <span>Table Settings</span>*/}
                                {/*</FilterButton>*/}
                                {/*</td>*/}
                                <td>
                                    <FilterButton
                                        onClick={() => this.openFilterModal()}
                                    >
                                        <i className={'material-icons'}>
                                            filter_list
                                        </i>
                                        <span>Filter</span>
                                    </FilterButton>
                                </td>
                            </tr>
                            <tr className="headers">
                                {eventsTableHeaders.map(header => (
                                    <td key={header.key}>
                                        {header.value}{' '}
                                        {header.showArrowSort && (
                                            <Sorter
                                                sortingKey={header.key}
                                                currentSortingKey={
                                                    this.state.sort
                                                        .currentSortingKey
                                                }
                                                onSort={this.updateSort}
                                            />
                                        )}
                                    </td>
                                ))}
                            </tr>
                            {loading ? (
                                <tr>
                                    <td colSpan={eventsTableHeaders.length}>
                                        <SpinLoader />
                                    </td>
                                </tr>
                            ) : (
                                events.map(event => (
                                    <tr
                                        className="data"
                                        key={event.eventReference}
                                    >
                                        <td>
                                            <PlayerIdAndName
                                                username={event.username}
                                                id={event.playerId}
                                            />
                                        </td>
                                        <td>
                                            <FriendlyDate
                                                timestamp={event.eventTimestamp}
                                            />
                                        </td>
                                        <td>{event.friendlyEventType}</td>
                                        <td>{event.description}</td>
                                        <td>
                                            {(RuleCategory.ANTI_MONEY_LAUNDERING ===
                                                ruleCategory && (
                                                <AlertLevel
                                                    level={
                                                        event.transactionRisk
                                                            .amlRisk
                                                    }
                                                />
                                            )) ||
                                                (RuleCategory.RESPONSIBLE_GAMBLING ===
                                                    ruleCategory && (
                                                    <AlertLevel
                                                        level={
                                                            event
                                                                .transactionRisk
                                                                .rgRisk
                                                        }
                                                    />
                                                )) ||
                                                (RuleCategory.FRAUD ===
                                                    ruleCategory && (
                                                    <AlertLevel
                                                        level={
                                                            event
                                                                .transactionRisk
                                                                .fraudRisk
                                                        }
                                                    />
                                                ))}
                                        </td>
                                        <td>
                                            <AlertLevel
                                                level={
                                                    event.playerRisk.averageRisk
                                                }
                                            />
                                        </td>
                                        <td>
                                            <TransparentButton
                                                onClick={() =>
                                                    this.openInfoModal(event)
                                                }
                                            >
                                                Details
                                            </TransparentButton>
                                        </td>
                                    </tr>
                                )) || (
                                    <tr
                                        className="data"
                                        style={{ textAlign: 'center' }}
                                    >
                                        <td colSpan={eventsTableHeaders.length}>
                                            <EmptyList />
                                        </td>
                                    </tr>
                                )
                            )}

                            <Pager
                                pagination={pagination}
                                onPaginationChanged={updatedPagination =>
                                    this.updatePagination(updatedPagination)
                                }
                                colspan={eventsTableHeaders.length}
                            />
                        </tbody>
                        <ModalContainer
                            title={this.getModalTitle()}
                            content={this.getModalContent()}
                            ref={this.modalRef}
                        />
                    </table>
                </div>
            </Fragment>
        );
    }
}

const mapStateToProps = state => {
    const { data, sort, pagination, loading, error } = state.events;
    const jurisdictionCode = getSelectedJurisdictionCode(state);
    const brandId = getSelectedBrandId(state);

    return {
        jurisdictionCode,
        brandId,
        events: data,
        sort,
        pagination,
        loading,
        error
    };
};
const mapDispatchToProps = dispatch => ({
    fetchEvents: (
        ruleCategory,
        sort,
        pagination,
        jurisdictionCode,
        brandId,
        type
    ) =>
        dispatch(
            fetchEvents({
                ruleCategory,
                sort,
                pagination,
                jurisdictionCode,
                brandId,
                eventType: type
            })
        )
});
export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(EventListContainer)
);
